import { toHumanString } from 'human-readable-numbers'
import { get } from 'lodash'
import accounting from 'accounting'
import { ADD_ON_RESOURCES, UNLIMITED, isMonthlyFor, isSupportedFor, namedAmount } from 'app/views/accounts/addOns/addOnResources'

// Plans no longer purchasable. Drives the "you've got the <legacy plan>!"
// reassurance message in PlansHeaderSection for grandfathered customers.
export const NON_OFFERED_PLANS = [
  'FA-LITE',
  'FA-LITE-PLUS',
  'FA-STANDARD',
  'FA-STANDARD-PLUS',
  'FA-MAX',
  'FA-MAX-PLUS',
  'FA-COMPED-STANDARD',
  'FA-EMPLOYEE',
  'WA-FA-ONLY-BACKER',
  'WA-FA-BUNDLE-BACKER',
  'WA-FA-TEAM-BACKER'
]

// The three currently offered tiers. Each tier has two billing cycles
// (annual + monthly); the toggle on the plans page swaps the displayed
// price. Per-SKU constants below are keyed by the annual SKU as the
// tier identity — monthly variants inherit color / description.
export const ALL_PLANS_BY_SKU = ['FA-STARTER', 'FA-SOLO', 'FA-TEAM']

// Tier-only slugs (no cycle suffix). Used as the canonical "which tier"
// identifier on surfaces that handle cycle separately — e.g., the
// PlanChangeView picker's `targetTier`, and as a whitelist for parsing
// `/plans/change/:slug?` route params back into tier + cycle.
export const NEW_TIER_SLUGS = ['starter', 'solo', 'team']

// Subset of NEW_TIER_SLUGS whose plans carry the pro-plus-collection
// icon set. Used by the FR-021 click-gate on /plans cards and the
// PlanChangeView picker to disable transitions that would drop a
// Pro+ customer to a non-Pro+ tier. Keep in sync with whatever
// `iconCollection` is wired to each tier's plan record.
export const PRO_PLUS_TIER_SLUGS = ['solo', 'team']

// Headline savings figure surfaced on the cycle toggles ("Save X%").
// Lives here so both the header toggle and the compare-table toggle
// stay in sync. Number is the rounded discount of annual vs 12×monthly
// for the Solo tier — same number we use in copy across the page.
export const ANNUAL_SAVINGS_PERCENT = 50

export const MONTHLY_SKU_FOR_ANNUAL = {
  'FA-STARTER': 'FA-STARTER-MONTHLY',
  'FA-SOLO': 'FA-SOLO-MONTHLY',
  'FA-TEAM': 'FA-TEAM-MONTHLY'
}

export const ANNUAL_SKU_FOR_MONTHLY = {
  'FA-STARTER-MONTHLY': 'FA-STARTER',
  'FA-SOLO-MONTHLY': 'FA-SOLO',
  'FA-TEAM-MONTHLY': 'FA-TEAM'
}

export const PLAN_COLOR_BY_SKU = {
  'FA-STARTER': 'blue',
  'FA-SOLO': 'yellow',
  'FA-TEAM': 'teal'
}

export const PLAN_ICONSTYLE_BY_SKU = {
  'FA-STARTER': 'solid',
  'FA-SOLO': 'solid',
  'FA-TEAM': 'solid'
}

export const PLAN_TAGLINE_BY_SKU = {
  'FA-STARTER': 'For hosted projects',
  'FA-SOLO': 'For individual designers + devs',
  'FA-TEAM': 'For teams shipping together'
}

// Pageviews helper: 50000 → "50K", 1000000 → "1M".
export function formatPageviews(n) {
  if (n >= 1_000_000) return `${n / 1_000_000}M`
  if (n >= 1_000) return `${n / 1_000}K`
  return String(n)
}

// Builds the property payload for the `plans_overview_view` event. Gates
// `user_plan_sku` on `has_active_subscription` (already in the determinations
// props) rather than the presence of a subscription record — a canceled
// subscription is still returned with its `plan`, so keying off the sku alone
// would tag a lapsed user with their old paid SKU while `is_free` reports free.
export function buildPlansOverviewEventProps({ determinationsProps, subscription }) {
  const props = { ...determinationsProps }
  const sku = get(subscription, ['plan', 'sku'])
  if (sku && props.has_active_subscription) props.user_plan_sku = sku
  return props
}

// Bandwidth helper: bytes → GB string. One decimal under 10GB, rounded
// above. Mirrors the level of precision the Account-page progress bar
// uses for npm bandwidth usage. Zero is rendered without a decimal so
// it visually matches the integer-valued count rows (Kits, Custom Icons,
// Pageviews) when the customer has no usage this month.
export function formatBandwidth(bytes) {
  if (bytes == null) return ''
  const gb = bytes / 1024 ** 3
  if (gb === 0) return '0GB'
  return gb >= 10 ? `${Math.round(gb)}GB` : `${gb.toFixed(1)}GB`
}

// Feature list shown beneath the CTA on each plan card. Heading describes
// what the tier adds; rows are { label, annualOnly? }. The card looks up the
// ANNUAL variant of each tier (canonical capabilities) so the list stays
// stable across cycle toggles. Items flagged `annualOnly` dim and re-label
// as "Annual only" when the monthly cycle is selected.
export const PLAN_FEATURES_BY_SKU = {
  'FA-STARTER': (plan) => ({
    heading: 'Includes',
    rows: [
      { icon: 'faCloud', label: 'All Pro icons via CDN' },
      { icon: 'faToolbox', label: `${plan.kits} project kits` },
      { icon: 'faAnalytics', label: `${formatPageviews(plan.monthlyPageviews)} pageviews/month` },
      { icon: 'faRobot', label: 'AI agent tools' },
      { icon: 'faUser', label: `${plan.seats} seat${plan.seats === 1 ? '' : 's'}` }
    ]
  }),
  'FA-SOLO': (plan) => ({
    heading: 'Everything in Starter, plus',
    rows: [
      { icon: 'faSparkles', label: 'All Pro+ icons' },
      { icon: 'faDownload', label: 'Icon downloads, npm, Figma', annualOnly: true },
      { icon: 'faAward', label: 'Perpetual license', annualOnly: true },
      { icon: 'faGauge', label: `${plan.kits} kits, ${formatPageviews(plan.monthlyPageviews)} pageviews` },
      { icon: 'faCloudArrowUp', label: `${plan.customIcons} custom icons` }
    ]
  }),
  'FA-TEAM': (plan) => ({
    heading: 'Everything in Solo, plus',
    rows: [
      { icon: 'faUsers', label: `${plan.seats} seats, scales to 250` },
      { icon: 'faTags', label: 'Graduated seat pricing' },
      { icon: 'faShield', label: 'Team roles + permissions' },
      { icon: 'faGauge', label: `${plan.kits} kits, ${formatPageviews(plan.monthlyPageviews)} pageviews` },
      { icon: 'faCloudArrowUp', label: `${plan.customIcons} custom icons` }
    ]
  })
}

// Modal (and other compact contexts) descriptions. Monthly variants carry
// their own line rather than borrowing the tier's (as display data otherwise
// does): the download gate pairs a monthly plan with its annual counterpart,
// and identical descriptions there read as a rendering bug.
export const PLAN_DESCRIPTION_BY_SKU = {
  'FA-STARTER': 'Pro icons, hosted via CDN.',
  'FA-STARTER-MONTHLY': 'Pro icons, hosted via CDN.',
  'FA-SOLO': 'The full Font Awesome library and toolkit.',
  'FA-SOLO-MONTHLY': 'The full Font Awesome library, hosted on our CDN.',
  'FA-TEAM': 'The full Font Awesome library and toolkit for teams.',
  'FA-TEAM-MONTHLY': 'The full Font Awesome library for teams, hosted on our CDN.'
}

// Where customers go when they need more than self-serve can handle —
// SeatPickerPanel's "More than N seats?" link on the buy and plan-change
// pages, EnterpriseContactCard on /plans, and AddSeatsView's Enterprise
// CTAs all target this. Centralized here so the contact surfaces stay
// consistent. mailto: with a pre-filled subject so the customer's reply
// already routes to the right inbox + thread.
export const ENTERPRISE_CONTACT_URL = "mailto:hello@fontawesome.com?subject=I'm interested in an Enterprise plan, Font Awesome!"

// One-sentence "Includes:" copy for the buy-page order summary. Lives
// alongside the modal descriptions so all per-tier prose stays in one
// file, but kept as its own export: the buy page confirms a choice the
// user has already made, so it can be richer than the modal subtitle.
// Keyed by full SKU (not normalized to annual) because Solo's monthly
// variant intentionally describes a narrower feature set.
export const PLAN_INCLUDES_BY_SKU = {
  'FA-STARTER': 'The Font Awesome Pro library, plus Kits, Custom Icons, and the Icon Wizard, all hosted on our CDN.',
  'FA-STARTER-MONTHLY': 'The Font Awesome Pro library, plus Kits, Custom Icons, and the Icon Wizard, all hosted on our CDN.',
  'FA-SOLO': 'The full Font Awesome library and toolkit: the Icon Wizard, downloads, npm, Figma, and a perpetual license.',
  'FA-SOLO-MONTHLY': 'The full Font Awesome library, plus Kits and the Icon Wizard, hosted on our CDN.',
  'FA-TEAM': 'The full Font Awesome library and toolkit, plus multiple seats and team roles.',
  'FA-TEAM-MONTHLY': 'The full Font Awesome library via CDN, plus multiple seats and team roles.'
}

// One slug per SKU. Six routes total, three tiers × two billing cycles.
// `routes/index.js` reduces this list into per-slug `plans.<slug>` routes.
export const ALL_PLAN_SLUGS = ['starter', 'starter-monthly', 'solo', 'solo-monthly', 'team', 'team-monthly']

export const ALL_PLANS_BY_PROPER_NAME = ['Starter', 'Solo', 'Team']

// Annual Solo/Team only — Starter and the monthly variants are CDN-only (no perpetual).
export const PLANS_WITH_PERPETUAL_LICENSE = ['Solo (annual)', 'Team (annual)']

// CDN-only plans: Starter (both cycles) plus monthly Solo and Team.
export const PLANS_WITH_LIMITED_LICENSE = ['Starter (annual)', 'Starter (monthly)', 'Solo (monthly)', 'Team (monthly)']

export function getAutoAppliedDiscounts() {
  return AUTO_APPLIED_DISCOUNT_CODES
}

export function getKickstarterDiscounts() {
  return AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES
}

export function getPromoCodes() {
  return PROMO_CODES
}

// Put any active promo codes here. Any promo code on the query string will take precidence.
// If we have a promo code we want applied across all plans, you'll want to make sure it's the
// only one in this list.
export const AUTO_APPLIED_DISCOUNT_CODES = []

// Used for Kickstarter backers. These discounts are applied automatically.
export const AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES = []

// This is used when we want to show a promo code to certain accounts
export const PROMO_CODES = {
  0: { id: null, code: null }
}

// This is used when we want to show a promo code to certain accounts
export function shouldHavePromoCode(account, subscription) {
  // return account && get(subscription, 'status', '') === 'active' && get(subscription, 'type', '') !== 'free'
  return false
}

// This is used when we want to show a promo code to certain accounts
export function getDiscountPercent(account, subscription) {
  return '0'
}

export const HANDLE_FUNCTIONS = {
  asText(params = {}) {
    const { field } = params

    return (plan) => {
      return { included: true, decorator: plan[field] }
    }
  },

  asBoolean(params = {}) {
    const { field } = params

    return (plan) => {
      return { included: plan[field] }
    }
  },

  asUnlimitedNumber(params = {}) {
    const { field } = params

    return (plan) => {
      const number = plan[field]

      return { included: number !== 0, decorator: this.unlimitedNumberFormatted(number) }
    }
  },

  asDeepFieldMatch(params = {}) {
    const { field, match } = params

    return (plan) => {
      const value = get(plan, field.split(' '), false)

      return { included: match.includes(value) }
    }
  },

  asHumanReadableNumber(params = {}) {
    const { field } = params

    return (plan) => {
      const number = plan[field]

      return { included: number !== 0, decorator: toHumanString(number) }
    }
  },

  asDataPerMonth(params = {}) {
    const { field, numberSuffix = 'GB' } = params

    return (plan) => {
      const total = plan[field]

      return { included: total !== 0, decorator: `${total} ${numberSuffix}` }
    }
  },

  asPriceWithSuffix(params = {}) {
    const { field, suffix, numberPrecision } = params

    return (plan) => {
      const number = get(plan, field, { amount: 0 })

      if (number !== null) {
        return { included: true, decorator: `${this.formatMoney(number.amount, numberPrecision)}${suffix}` }
      } else {
        return { included: false }
      }
    }
  },

  // Additional Seats: shows the seat-upgrade price as "$X/seat", with a
  // "(graduated)" suffix (hidden on phones) + tooltip on Team annual where banded pricing
  // applies. Tiers without seat upgrades render as not included.
  asAdditionalSeats() {
    return (plan) => {
      if (!get(plan, 'seatUpgradeEnabled')) {
        return { included: false }
      }
      const price = get(plan, ['seatUpgradePrice', 'amount'])
      if (price === null || price === undefined) {
        return { included: false }
      }
      const formatted = `${this.formatMoney(price, 0)}/seat`
      if (plan.sku === 'FA-TEAM') {
        return {
          included: true,
          decorator: formatted,
          // Its own field so the table can drop it on phones: the Team column is
          // too narrow for "(graduated)", which pushed /plans into sideways
          // scrolling at 390px. The tooltip still carries the detail there.
          decoratorSuffix: '(graduated)',
          tooltip: 'Graduated pricing: $20/yr for seats 4–10, $15/yr for seats 11–20, $10/yr for seats 21+.'
        }
      }
      return { included: true, decorator: formatted }
    }
  },

  // Like asPriceWithSuffix, but the suffix is derived from `plan.annual` —
  // "/yr" for annual plans, "/mo" for monthly. Matches the "{value} {unit}"
  // shape used by asDataPerMonth so the price row reads consistently
  // alongside the other unit-bearing rows in the table.
  asPriceWithCycleSuffix(params = {}) {
    const { field, numberPrecision } = params

    return (plan) => {
      const number = get(plan, field, { amount: 0 })

      if (number !== null) {
        const suffix = plan.annual === false ? '/mo' : '/yr'
        return { included: true, decorator: `${this.formatMoney(number.amount, numberPrecision)}${suffix}` }
      } else {
        return { included: false }
      }
    }
  },

  unlimitedNumberFormatted(number) {
    if (number < 0) {
      return 'Unlimited'
    } else {
      return number
    }
  },

  formatMoney(money, precision = 0) {
    const formattedMoney = (money || 0) / 100

    return accounting.formatMoney(formattedMoney, { precision: precision })
  }
}

export const TABLE_ROWS = [
  [
    {
      featureName: 'Plan',
      featureDescription: 'Plan name',
      featureIcon: 'faFontAwesome',
      handleFunction: HANDLE_FUNCTIONS.asText({ field: 'name' })
    },
    {
      featureName: 'Price',
      featureDescription: 'Subscription price',
      featureIcon: 'faCreditCard',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithCycleSuffix({ field: 'price' })
    },
    {
      featureName: 'Team Seats',
      featureDescription: 'Starting number of seats included in your team',
      featureIcon: 'faUserGroup',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'seats' })
    },
    {
      featureName: 'Project Kits',
      featureDescription: 'Kits included for your projects',
      featureIcon: 'faSuitcase',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'kits' })
    },
    {
      featureName: 'Custom Icons',
      featureDescription: 'Upload and use your own icons',
      featureIcon: 'faCloudArrowUp',
      handleFunction: HANDLE_FUNCTIONS.asUnlimitedNumber({ field: 'customIcons' })
    },
    {
      featureName: 'Pageviews',
      featureDescription: 'Monthly Kit pageviews included',
      featureIcon: 'faSignalBars',
      handleFunction: HANDLE_FUNCTIONS.asHumanReadableNumber({ field: 'monthlyPageviews' })
    },
    {
      featureName: 'Bandwidth',
      featureDescription: 'Monthly package bandwidth included',
      featureIcon: 'faBox',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asDataPerMonth({ field: 'monthlyBandwidth', numberSuffix: 'GB' })
    }
  ],
  [
    {
      featureName: 'Additional Seats',
      featureDescription: 'Price per additional team member',
      featureIcon: 'faCircleUser',
      handleFunction: HANDLE_FUNCTIONS.asAdditionalSeats()
    },
    {
      featureName: '+250K Pageviews',
      featureDescription: 'Price per extra 250k pageviews per month',
      featureIcon: 'faSignalBars',
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'overagePageviewPrice', suffix: '/mo', numberPrecision: 0 })
    },
    {
      featureName: 'Bandwidth/mo',
      featureDescription: 'Price per extra GB of bandwidth per month',
      featureIcon: 'faBox',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asPriceWithSuffix({ field: 'overageBandwidthPrice', suffix: '/GB', numberPrecision: 2 })
    }
  ],
  [
    {
      featureName: 'Pro Icons',
      featureDescription: 'Full Pro icon library',
      featureIcon: 'faIcons',
      handleFunction: HANDLE_FUNCTIONS.asDeepFieldMatch({ field: 'iconCollection name', match: ['pro-collection', 'pro-plus-collection'] })
    },
    {
      featureName: 'Pro+ Icons',
      featureDescription: 'Full Pro+ icon library',
      featureIcon: 'faShapes',
      handleFunction: HANDLE_FUNCTIONS.asDeepFieldMatch({ field: 'iconCollection name', match: ['pro-plus-collection'] })
    },
    {
      featureName: 'Icon Wizard',
      featureDescription: 'Combine and remix icons into something new',
      featureIcon: 'faHandHoldingMagic',
      // Keyed on proEnabled, not proDownloadEnabled — the Wizard is unlocked by
      // any paid plan, both cycles, so this is not annualOnly.
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proEnabled' })
    },
    {
      featureName: 'Icon Download',
      featureDescription: 'Download icons for offline (and online) use',
      featureIcon: 'faArrowDownToLine',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'Hosted npm',
      featureDescription: 'Hosted package registries (npm, Yarn)',
      featureIcon: 'faBox',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'privateRegistriesEnabled' })
    },
    {
      featureName: 'Pro Icons via API',
      featureDescription: 'API tokens that can fetch Pro icons',
      featureIcon: 'faKey',
      annualOnly: true,
      // Permits.Types.ProIconTokenScopes defers to AccessToProDownload, which
      // reads pro_download_enabled, so this row follows Icon Download.
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'Perpetual License',
      featureDescription: 'Keep icons released during your subscription',
      featureIcon: 'faFile',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'perpetualLicenseEnabled' })
    },
    {
      featureName: 'Figma',
      featureDescription: 'Use icons in Figma with our plugin or component file',
      featureIcon: 'faPenNib',
      annualOnly: true,
      handleFunction: HANDLE_FUNCTIONS.asBoolean({ field: 'proDownloadEnabled' })
    },
    {
      featureName: 'WordPress',
      featureDescription: 'Official WordPress plugin for easy setup',
      featureIcon: 'faPuzzle',
      // WordPress is included with every plan tier
      handleFunction: () => ({ included: true })
    },
    {
      featureName: 'AI Agent Tools',
      featureDescription: 'Skills for Claude, Cursor, Copilot, and more',
      featureIcon: 'faRobot',
      handleFunction: () => ({ included: true })
    }
  ]
]

const ADD_ON_DESCRIPTIONS = {
  kits: 'Add more Kits to your plan',
  icon_uploads: 'Add more Custom Icons to your plan',
  pageviews: 'Prepay for extra pageviews each month',
  bandwidth: 'Prepay for extra GB of bandwidth each month'
}

// Cents to dollars, keeping cents only when there are some: "$18", "$1.50".
function formatAddOnPrice(cents) {
  return HANDLE_FUNCTIONS.formatMoney(cents, cents % 100 === 0 ? 0 : 2)
}

// A free plan can't hold add-ons, a CDN-only plan has no bandwidth to add to,
// and an unlimited allowance has nothing to raise, so each prices only where
// the add-ons page would actually sell it.
function addOnPriceFor(price, resource) {
  return (plan) => {
    const subscription = { plan }

    if (!get(plan, ['price', 'amount'])) return { included: false }
    if (!isSupportedFor(subscription, resource.key)) return { included: false }
    if (plan[resource.planField] === UNLIMITED) return { included: true, decorator: 'Unlimited' }

    const billedMonthly = plan.annual === false
    const cents = billedMonthly ? price.monthlyPriceCents : price.annualPriceCents

    if (cents == null) return { included: false }
    if (billedMonthly) return { included: true, decorator: `${formatAddOnPrice(cents)}/mo` }
    if (!isMonthlyFor(resource.key)) return { included: true, decorator: `${formatAddOnPrice(cents)}/yr` }

    // Overages for monthly allowances are priced per month, so the annual
    // add-on price is shown per month too, keeping the two comparable.
    return {
      included: true,
      decorator: `${formatAddOnPrice(Math.round(cents / 12))}/mo`,
      tooltip: `Billed yearly at ${formatAddOnPrice(cents)}.`
    }
  }
}

// One row per add-on on sale, in the add-ons page's order. Built from the
// catalog the plans endpoint sends, so the pack size and price follow it. An
// add-on marked `onPlansTable: false` (Pro+) has no row: it is offered on its
// own card instead.
export function addOnTableRows(addOnPrices) {
  return ADD_ON_RESOURCES.flatMap((resource) => {
    if (resource.onPlansTable === false) return []

    const price = addOnPrices.find((p) => p.resource === resource.key)
    if (!price) return []

    return [
      {
        featureName: `+${namedAmount(resource.key, price.incrementSize)}`,
        featureDescription: ADD_ON_DESCRIPTIONS[resource.key],
        featureIcon: resource.iconName,
        handleFunction: addOnPriceFor(price, resource)
      }
    ]
  })
}
